import { SVG_ENUM, TOAST_TYPES_ENUM } from "enums";
import { EditPersonalDataModal } from "features/users";
import { useDisconnectSocial, useGetSocialAccounts } from "hooks/auth";
import React, { useEffect, useState } from "react";
import { Button, Modal, SVG, showToastWithLoading } from "shared";
import { UserType } from "types/UsersTypes";
import { getPrefixFromUuid } from "utils";
import { useGetPhonePrefixes } from "hooks/phone";
import { useSearchParams } from "react-router-dom";

interface LoginOptionsProps {
  user: UserType | undefined;
  handleGoBack: () => void;
}

const PROVIDER_LABELS: Record<string, string> = {
  google: "Google",
  apple: "Apple",
};

const CONNECT_ERROR_MESSAGES: Record<string, string> = {
  already_connected:
    "jest już połączone z innym kontem w naszym serwisie.",
  connect_failed: "nie zostało połączone. Spróbuj ponownie.",
};

const LoginOptions: React.FC<LoginOptionsProps> = ({ user, handleGoBack }) => {
  const { data: prefixNumbers } = useGetPhonePrefixes();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [disconnectProvider, setDisconnectProvider] = useState<
    "google" | "apple" | null
  >(null);
  const { mutate: disconnectSocial } = useDisconnectSocial();
  const { data: socialAccounts } = useGetSocialAccounts();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const errorCode = searchParams.get("error");
    const connectedProvider = searchParams.get("connected");
    const provider = searchParams.get("provider") ?? connectedProvider;
    const providerLabel = provider ? (PROVIDER_LABELS[provider] ?? provider) : "Konto";

    if (errorCode) {
      const suffix =
        CONNECT_ERROR_MESSAGES[errorCode] ?? "nie zostało połączone. Spróbuj ponownie.";
      const message = `Konto ${providerLabel} ${suffix}`;

      showToastWithLoading(
        { message, id: "social-connect" },
        { message, type: TOAST_TYPES_ENUM.ERROR, id: "social-connect" },
      );
    } else if (connectedProvider) {
      const message = `Konto ${providerLabel} zostało połączone.`;

      showToastWithLoading(
        { message, id: "social-connect" },
        { message, type: TOAST_TYPES_ENUM.SUCCESS, id: "social-connect" },
      );
    } else {
      return;
    }

    setSearchParams(
      (prev) => {
        prev.delete("error");
        prev.delete("connected");
        prev.delete("provider");
        return prev;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasEmail = !!user?.email;
  const googleAccount = socialAccounts?.find((a) => a.provider === "google");
  const appleAccount = socialAccounts?.find((a) => a.provider === "apple");

  const renderSocialSection = (
    provider: "google" | "apple",
    account: { name: string; email: string } | undefined,
    svgIcon: SVG_ENUM,
    connectLabel: string,
  ) => {
    if (account) {
      return (
        <div className="profile__social-connected">
          <p className="profile__social-connected-name">{account.name}</p>
          <div className="profile__social-connected-row">
            <span className="profile__social-connected-email">
              {account.email}
            </span>
            <span
              className="profile__social-connected-remove"
              onClick={() => setDisconnectProvider(provider)}
            >
              Usuń metodę logowania
            </span>
          </div>
        </div>
      );
    }

    return (
      <button
        type="button"
        className="login-wrapper__social-btn"
        onClick={() => {
          window.location.href = `${process.env.REACT_APP_API_URL}/auth/${provider}/connect`;
        }}
      >
        <SVG type={svgIcon} />
        {connectLabel}
      </button>
    );
  };

  return (
    <div className="profile__right-wrapper">
      <EditPersonalDataModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={user || null}
      />
      <Modal
        isOpened={!!disconnectProvider}
        onClose={() => setDisconnectProvider(null)}
      >
        <>
          <div className="modal-heading">Usuń metodę logowania</div>
          <div className="modal-description">
            Czy jesteś pewien, że chcesz usunąć metodę logowania przez{" "}
            {disconnectProvider === "google" ? "Google" : "Apple"}?
          </div>
          <div className="modal-buttons">
            <Button
              className="button--stroke button--rounded"
              onClick={() => setDisconnectProvider(null)}
              label="Anuluj"
            />
            <Button
              className="button--black button--rounded"
              onClick={() => {
                if (disconnectProvider) {
                  disconnectSocial({ provider: disconnectProvider });
                  setDisconnectProvider(null);
                }
              }}
              label="Potwierdź"
            />
          </div>
        </>
      </Modal>
      <div className="profile__right-content">
        <div className="profile__right-back" onClick={() => handleGoBack()}>
          <SVG type={SVG_ENUM.ARROW_LEFT} /> Wróć
        </div>
        <h2 className="profile__menu-header">
          <span className="profile__menu-icon profile__menu-icon--right">
            <SVG type={SVG_ENUM.LOGIN_OPTIONS} />
          </span>
          Opcje logowania
        </h2>

        <p className="profile__section-title">
          Logowanie za pomocą kodu dostępu na numer telefonu
        </p>
        <table className="profile__table">
          <tr>
            <td className="profile__table-label">Numer telefonu:</td>
            <td className="profile__table-cell">
              {user?.phonePrefixUuid
                ? getPrefixFromUuid(user.phonePrefixUuid, prefixNumbers)
                : ""}{" "}
              {user?.phoneNumber || "-"}
            </td>
          </tr>
          <tr>
            <td className="profile__table-label">Email:</td>
            <td className="profile__table-cell">{user?.email || "-"}</td>
          </tr>
        </table>

        {!hasEmail && !googleAccount && !appleAccount ? (
          <p className="profile__social-no-email profile__social-no-email--top">
            Aby połączyć konto z Apple lub Google, potrzebny jest adres email.{" "}
            <span
              className="profile__social-no-email-link"
              onClick={() => setIsEditModalOpen(true)}
            >
              Kliknij tutaj, aby go dodać.
            </span>
          </p>
        ) : (
          <>
            <p className="profile__section-title profile__section-title--alternative">
              Logowanie za pomocą konta Google
            </p>
            {renderSocialSection(
              "google",
              googleAccount,
              SVG_ENUM.GOOGLE_ICON,
              "Połącz konto z kontem Google",
            )}

            <p className="profile__section-title profile__section-title--alternative">
              Logowanie za pomocą konta Apple
            </p>
            {renderSocialSection(
              "apple",
              appleAccount,
              SVG_ENUM.APPLE_ICON,
              "Połącz konto z kontem Apple",
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LoginOptions;
